import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import {
  mapFoodicsProductToImportPayload,
  mapFoodicsModifierToImportPayload,
  useFoodicsMenuEntities,
  useImportFoodicsMenuEntities,
} from "src/api/integrations/foodics";
import type {
  FoodicsMenuProductEntity,
  FoodicsModifierOptionEntity,
  ImportFoodicsMenuEntitiesBody,
} from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { StepIndicatorCard } from "../../step-indicator-card";
import { ImportModeCard } from "../../import-mode-card";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import CategoryIcon from "@mui/icons-material/Category";
import CheckIcon from "@mui/icons-material/Check";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import TuneIcon from "@mui/icons-material/Tune";

type InnerStep = 1 | 2 | 3;

const FoodicsMenuImportEdit = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const isArabic = i18n.language === "ar";
  const currentLang = i18n.language || "en";

  const [activeStep, setActiveStep] = useState<InnerStep>(1);
  const [productsRecipeMode, setProductsRecipeMode] = useState<boolean | null>(
    null
  );
  const [modifiersRecipeMode, setModifiersRecipeMode] = useState<
    boolean | null
  >(null);

  // Products selection state
  const [productsPage, setProductsPage] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(100);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const seenProductIdsRef = useRef<Set<string>>(new Set());
  const productsCacheRef = useRef<Map<string, FoodicsMenuProductEntity>>(
    new Map()
  );

  // Modifiers selection state
  const [modifiersPage, setModifiersPage] = useState(0);
  const [modifiersPerPage, setModifiersPerPage] = useState(100);
  const [selectedModifierIds, setSelectedModifierIds] = useState<string[]>([]);
  const seenModifierIdsRef = useRef<Set<string>>(new Set());
  const modifiersCacheRef = useRef<
    Map<string, FoodicsModifierOptionEntity & { external_id: string }>
  >(new Map());

  // Auto-import categories flag
  const autoImportedRef = useRef(false);

  const { data, isLoading, refetch } = useFoodicsMenuEntities({
    categories_page: 1,
    categories_per_page: 500,
    products_page: productsPage + 1,
    products_per_page: productsPerPage,
    include_modifier_options: 1,
    modifier_options_page: modifiersPage + 1,
    modifier_options_per_page: modifiersPerPage,
  });

  const {
    mutate: importItems,
    status: importStatus,
    reset: resetImport,
  } = useImportFoodicsMenuEntities();

  const categories = data?.data?.categories ?? [];
  const products = data?.data?.products ?? [];
  const productsPagination = data?.data?.products_pagination;
  const modifiers = data?.data?.modifier_options ?? [];
  const modifiersPagination = data?.data?.modifier_options_pagination;
  const modifiersFetchFailed =
    data?.data?.modifier_options_fetch_failed === true;

  const importedCategoriesCount = categories.filter(
    (c) => c.already_imported
  ).length;

  const getLocalized = (text?: { en?: string; ar?: string }): string =>
    text?.[currentLang as "en" | "ar"] || text?.en || text?.ar || "-";

  // Auto-import categories on mount
  useEffect(() => {
    if (autoImportedRef.current || categories.length === 0) return;
    const unimported = categories.filter((c) => !c.already_imported);
    if (unimported.length === 0) {
      autoImportedRef.current = true;
      return;
    }
    autoImportedRef.current = true;
    importItems({
      categories: unimported.map((c) => ({
        id: c.external_id,
        name: c.name,
        ...(c.sku?.trim() ? { sku: c.sku.trim() } : {}),
      })),
      products: [],
    });
  }, [categories]);

  // Auto-select products not yet imported
  useEffect(() => {
    const newIds: string[] = [];
    products.forEach((row) => {
      productsCacheRef.current.set(row.external_id, row);
      if (!seenProductIdsRef.current.has(row.external_id)) {
        seenProductIdsRef.current.add(row.external_id);
        if (!row.already_imported) {
          newIds.push(row.external_id);
        }
      }
    });
    if (newIds.length > 0) {
      setSelectedProductIds((prev) => [...prev, ...newIds]);
    }
  }, [products]);

  // Auto-select modifiers not yet imported
  useEffect(() => {
    const newIds: string[] = [];
    modifiers.forEach((row) => {
      if (!row.external_id) return;
      modifiersCacheRef.current.set(
        row.external_id,
        row as FoodicsModifierOptionEntity & { external_id: string }
      );
      if (!seenModifierIdsRef.current.has(row.external_id)) {
        seenModifierIdsRef.current.add(row.external_id);
        if (!row.already_imported) {
          newIds.push(row.external_id);
        }
      }
    });
    if (newIds.length > 0) {
      setSelectedModifierIds((prev) => [...prev, ...newIds]);
    }
  }, [modifiers]);

  // Handle category auto-import result
  useEffect(() => {
    if (activeStep !== 1) return;
    if (importStatus === "success") {
      refetch();
      resetImport();
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        { type: "error", message: t("menuImport.importError") },
      ]);
      resetImport();
    }
  }, [importStatus, activeStep]);

  const handleProductsImport = () => {
    const payload = selectedProductIds
      .map((id) => productsCacheRef.current.get(id))
      .filter(
        (row): row is FoodicsMenuProductEntity =>
          !!row && !row.already_imported
      )
      .map((row) => mapFoodicsProductToImportPayload(row));

    if (payload.length === 0) {
      setActiveStep(3);
      return;
    }

    const body: ImportFoodicsMenuEntitiesBody = {
      categories: [],
      products: payload,
      ...(productsRecipeMode
        ? { recipe_product_ids: payload.map((p) => p.id) }
        : {}),
    };

    importItems(body, {
      onSuccess: () => {
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: t("foodicsWorkspace.menuImport.productsImported"),
          },
        ]);
        refetch();
        resetImport();
        setActiveStep(3);
      },
      onError: () => {
        setNotifications([
          ...notifications,
          { type: "error", message: t("menuImport.importError") },
        ]);
        resetImport();
      },
    });
  };

  const handleModifiersImport = () => {
    const payload = selectedModifierIds
      .map((id) => modifiersCacheRef.current.get(id))
      .filter(
        (
          row
        ): row is FoodicsModifierOptionEntity & { external_id: string } =>
          !!row && !row.already_imported
      )
      .map((row) => mapFoodicsModifierToImportPayload(row));

    if (payload.length === 0) {
      navigate("/integrations/foodics");
      return;
    }

    importItems(
      {
        categories: [],
        products: [],
        modifier_options: payload,
      },
      {
        onSuccess: () => {
          setNotifications([
            ...notifications,
            {
              type: "success",
              message: t("foodicsWorkspace.menuImport.modifiersImported"),
            },
          ]);
          navigate("/integrations/foodics");
        },
        onError: () => {
          setNotifications([
            ...notifications,
            { type: "error", message: t("menuImport.importError") },
          ]);
          resetImport();
        },
      }
    );
  };

  // Products table helpers
  const selectableProductIds = products
    .filter((row) => !row.already_imported)
    .map((row) => row.external_id);
  const allProductsSelected =
    selectableProductIds.length > 0 &&
    selectableProductIds.every((id) => selectedProductIds.includes(id));
  const someProductsSelected = selectableProductIds.some((id) =>
    selectedProductIds.includes(id)
  );

  const toggleSelectAllProducts = () => {
    if (allProductsSelected) {
      setSelectedProductIds((prev) =>
        prev.filter((id) => !selectableProductIds.includes(id))
      );
      return;
    }
    setSelectedProductIds((prev) => [
      ...prev,
      ...selectableProductIds.filter((id) => !prev.includes(id)),
    ]);
  };

  // Modifiers table helpers
  const selectableModifierIds = modifiers
    .filter((row) => !row.already_imported && row.external_id)
    .map((row) => row.external_id as string);
  const allModifiersSelected =
    selectableModifierIds.length > 0 &&
    selectableModifierIds.every((id) => selectedModifierIds.includes(id));
  const someModifiersSelected = selectableModifierIds.some((id) =>
    selectedModifierIds.includes(id)
  );

  const toggleSelectAllModifiers = () => {
    if (allModifiersSelected) {
      setSelectedModifierIds((prev) =>
        prev.filter((id) => !selectableModifierIds.includes(id))
      );
      return;
    }
    setSelectedModifierIds((prev) => [
      ...prev,
      ...selectableModifierIds.filter((id) => !prev.includes(id)),
    ]);
  };

  const forwardArrow = (
    <ArrowForwardIcon
      sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
      fontSize="small"
    />
  );

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {/* Header */}
      <Box>
        <Button
          variant="text"
          startIcon={
            <ArrowBackIcon
              sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
            />
          }
          onClick={() => navigate("/integrations/foodics")}
          sx={{ color: "#667085" }}
        >
          {t("foodicsWorkspace.editImport.backToWorkspace")}
        </Button>
        <Typography fontSize={24} fontWeight={600} color="#101828" mt={1}>
          {t("foodicsWorkspace.menuImport.title")}
        </Typography>
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.menuImport.subtitle")}
        </Typography>
      </Box>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* Step indicators */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <StepIndicatorCard
          stepNumber={1}
          icon={<CategoryIcon />}
          title={t("foodicsWorkspace.menuImport.categoriesTitle")}
          subtitle={t("foodicsWorkspace.menuImport.categoriesSubtitle")}
          isActive={activeStep === 1}
          isCompleted={activeStep > 1}
        />
        <Box
          display={{ xs: "none", sm: "flex" }}
          alignItems="center"
          color="#D0D5DD"
        >
          {forwardArrow}
        </Box>
        <StepIndicatorCard
          stepNumber={2}
          icon={<RestaurantMenuIcon />}
          title={t("foodicsWorkspace.menuImport.productsTitle")}
          subtitle={t("foodicsWorkspace.menuImport.productsSubtitle")}
          isActive={activeStep === 2}
          isCompleted={activeStep > 2}
        />
        <Box
          display={{ xs: "none", sm: "flex" }}
          alignItems="center"
          color="#D0D5DD"
        >
          {forwardArrow}
        </Box>
        <StepIndicatorCard
          stepNumber={3}
          icon={<TuneIcon />}
          title={t("foodicsWorkspace.menuImport.modifiersTitle")}
          subtitle={t("foodicsWorkspace.menuImport.modifiersSubtitle")}
          isActive={activeStep === 3}
          isCompleted={false}
        />
      </Stack>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* ========== Step 1: Categories ========== */}
      {activeStep === 1 && (
        <>
          <Alert
            severity="success"
            icon={<CheckIcon fontSize="inherit" />}
            sx={{
              bgcolor: "#ECFDF3",
              border: "1px solid #A6F4C5",
              borderRadius: "12px",
              "& .MuiAlert-icon": { color: "#12B76A" },
            }}
          >
            <Typography fontSize={14} fontWeight={600} color="#027A48">
              {t("foodicsWorkspace.menuImport.categoriesAutoTitle")}
            </Typography>
            <Typography fontSize={14} color="#027A48">
              {t("foodicsWorkspace.menuImport.categoriesAutoBody")}
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    t("menuImport.table.name"),
                    t("foodicsWorkspace.menuImport.statusHeader"),
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        color: "#475467",
                        bgcolor: "#F9FAFB",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={3} cellsLength={2} />
              ) : (
                <TableBody>
                  {categories.map((row) => (
                    <TableRow key={row.external_id}>
                      <TableCell>
                        <Typography fontSize={14}>
                          {getLocalized(row.name)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          icon={<CheckIcon fontSize="small" />}
                          label={t("menuImport.imported")}
                          sx={{
                            bgcolor: "#ECFDF3",
                            color: "#027A48",
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && categories.length === 0 && <NoData />}
          </TableContainer>

          {/* Footer */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            pt={2}
            borderTop="1px solid #EAECF0"
          >
            <Typography fontSize={14} color="#027A48">
              {t("foodicsWorkspace.menuImport.categoriesCount", {
                count: importedCategoriesCount,
              })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="tertiary"
                onClick={() => navigate("/integrations/foodics")}
              >
                {generalT("back")}
              </Button>
              <Button
                variant="contained"
                endIcon={forwardArrow}
                onClick={() => setActiveStep(2)}
              >
                {t("foodicsWorkspace.menuImport.nextProducts")}
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {/* ========== Step 2: Products ========== */}
      {activeStep === 2 && (
        <>
          {/* Recipe mode chooser */}
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("foodicsWorkspace.menuImport.productsRecipeQuestion")}
            </Typography>
            <Typography fontSize={12} color="#667085" mb={1.5}>
              {t("foodicsWorkspace.menuImport.productsRecipeHint")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <ImportModeCard
                icon={<MenuBookIcon fontSize="small" />}
                iconBgcolor="#FF8D3F"
                iconColor="#FFFFFF"
                title={t("foodicsWorkspace.menuImport.withRecipeTitle")}
                subtitle={t(
                  "foodicsWorkspace.menuImport.withRecipeSubtitle"
                )}
                isSelected={productsRecipeMode === true}
                onSelect={() => setProductsRecipeMode(true)}
              />
              <ImportModeCard
                icon={<BlockIcon fontSize="small" />}
                iconBgcolor="#F2F4F7"
                iconColor="#667085"
                title={t("foodicsWorkspace.menuImport.withoutRecipeTitle")}
                subtitle={t(
                  "foodicsWorkspace.menuImport.withoutRecipeSubtitle"
                )}
                isSelected={productsRecipeMode === false}
                onSelect={() => setProductsRecipeMode(false)}
              />
            </Stack>
            {productsRecipeMode === null && (
              <Typography fontSize={13} color="error" mt={1}>
                {t("foodicsWorkspace.menuImport.selectModeWarning")}
              </Typography>
            )}
          </Box>

          {/* Products table */}
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("foodicsWorkspace.menuImport.productsTitle")}
            </Typography>
            <Typography fontSize={12} color="#667085" mb={1.5}>
              {t("foodicsWorkspace.menuImport.productsHint")}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allProductsSelected}
                        indeterminate={
                          someProductsSelected && !allProductsSelected
                        }
                        onChange={toggleSelectAllProducts}
                        disabled={selectableProductIds.length === 0}
                      />
                    </TableCell>
                    {[
                      t("menuImport.table.name"),
                      t("menuImport.table.sku"),
                      t("menuImport.table.category"),
                      t("menuImport.table.price"),
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: "18px",
                          color: "#475467",
                          bgcolor: "#F9FAFB",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableLoadingSkeleton rowsLength={4} cellsLength={5} />
                ) : (
                  <TableBody>
                    {products.map((row) => (
                      <TableRow key={row.external_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedProductIds.includes(
                              row.external_id
                            )}
                            onChange={() =>
                              setSelectedProductIds((prev) =>
                                prev.includes(row.external_id)
                                  ? prev.filter(
                                      (id) => id !== row.external_id
                                    )
                                  : [...prev, row.external_id]
                              )
                            }
                            disabled={row.already_imported}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography fontSize={14} fontWeight={600}>
                              {getLocalized(row.name)}
                            </Typography>
                            {row.already_imported && (
                              <Chip
                                size="small"
                                color="success"
                                label={t("menuImport.imported")}
                              />
                            )}
                            {!row.already_imported && row.is_new && (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("menuImport.new")}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{row.sku || "—"}</TableCell>
                        <TableCell>
                          {row.category?.title
                            ? getLocalized(row.category.title)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {row.price != null
                            ? Number(row.price).toFixed(2)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
              {!isLoading && products.length === 0 && <NoData />}
            </TableContainer>

            <TablePagination
              component="div"
              labelRowsPerPage={generalT("rowsPerPage")}
              count={productsPagination?.total ?? products.length}
              page={
                Math.max((productsPagination?.current ?? productsPage + 1) - 1, 0)
              }
              onPageChange={(_, newPage) => setProductsPage(newPage)}
              rowsPerPage={productsPagination?.per ?? productsPerPage}
              onRowsPerPageChange={(event) => {
                setProductsPerPage(parseInt(event.target.value, 10));
                setProductsPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Box>

          {/* Footer */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            pt={2}
            borderTop="1px solid #EAECF0"
          >
            <Typography fontSize={14} color="#667085">
              {t("foodicsWorkspace.editImport.selectedOf", {
                selected: selectedProductIds.length,
                total: seenProductIdsRef.current.size,
              })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="tertiary"
                onClick={() => setActiveStep(1)}
                disabled={importStatus === "pending"}
              >
                {generalT("back")}
              </Button>
              <Button
                variant="contained"
                disabled={
                  productsRecipeMode === null || importStatus === "pending"
                }
                startIcon={
                  importStatus === "pending" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                endIcon={forwardArrow}
                onClick={handleProductsImport}
              >
                {t("foodicsWorkspace.editImport.importAndContinue")}
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {/* ========== Step 3: Modifiers ========== */}
      {activeStep === 3 && (
        <>
          {/* Recipe mode chooser */}
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("foodicsWorkspace.menuImport.modifiersRecipeQuestion")}
            </Typography>
            <Typography fontSize={12} color="#667085" mb={1.5}>
              {t("foodicsWorkspace.menuImport.modifiersRecipeHint")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <ImportModeCard
                icon={<MenuBookIcon fontSize="small" />}
                iconBgcolor="#FF8D3F"
                iconColor="#FFFFFF"
                title={t("foodicsWorkspace.menuImport.withRecipeTitle")}
                subtitle={t(
                  "foodicsWorkspace.menuImport.withRecipeSubtitle"
                )}
                isSelected={modifiersRecipeMode === true}
                onSelect={() => setModifiersRecipeMode(true)}
              />
              <ImportModeCard
                icon={<BlockIcon fontSize="small" />}
                iconBgcolor="#F2F4F7"
                iconColor="#667085"
                title={t("foodicsWorkspace.menuImport.withoutRecipeTitle")}
                subtitle={t(
                  "foodicsWorkspace.menuImport.withoutRecipeSubtitle"
                )}
                isSelected={modifiersRecipeMode === false}
                onSelect={() => setModifiersRecipeMode(false)}
              />
            </Stack>
            {modifiersRecipeMode === null && (
              <Typography fontSize={13} color="error" mt={1}>
                {t("foodicsWorkspace.menuImport.selectModeWarning")}
              </Typography>
            )}
          </Box>

          {/* Modifiers table */}
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("foodicsWorkspace.menuImport.modifiersTitle")}
            </Typography>
            <Typography fontSize={12} color="#667085" mb={1.5}>
              {t("foodicsWorkspace.menuImport.modifiersHint")}
            </Typography>

            {modifiersFetchFailed && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {t("menuImport.modifierOptionsFetchFailed")}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allModifiersSelected}
                        indeterminate={
                          someModifiersSelected && !allModifiersSelected
                        }
                        onChange={toggleSelectAllModifiers}
                        disabled={selectableModifierIds.length === 0}
                      />
                    </TableCell>
                    {[
                      t("menuImport.table.name"),
                      t("menuImport.table.sku"),
                      t("menuImport.table.modifierGroup"),
                      t("menuImport.table.price"),
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: "18px",
                          color: "#475467",
                          bgcolor: "#F9FAFB",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableLoadingSkeleton rowsLength={4} cellsLength={5} />
                ) : (
                  <TableBody>
                    {modifiers.map((row, index) => (
                      <TableRow
                        key={row.external_id ?? `mod-${index}-${row.sku}`}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              !!row.external_id &&
                              selectedModifierIds.includes(row.external_id)
                            }
                            onChange={() => {
                              if (!row.external_id) return;
                              setSelectedModifierIds((prev) =>
                                prev.includes(row.external_id!)
                                  ? prev.filter(
                                      (id) => id !== row.external_id
                                    )
                                  : [...prev, row.external_id!]
                              );
                            }}
                            disabled={
                              row.already_imported || !row.external_id
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography fontSize={14} fontWeight={600}>
                              {getLocalized(row.name)}
                            </Typography>
                            {row.already_imported && (
                              <Chip
                                size="small"
                                color="success"
                                label={t("menuImport.imported")}
                              />
                            )}
                            {!row.already_imported && row.is_new && (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("menuImport.new")}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{row.sku || "—"}</TableCell>
                        <TableCell>
                          {row.modifier?.title
                            ? getLocalized(row.modifier.title)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {row.price != null
                            ? Number(row.price).toFixed(2)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
              {!isLoading && modifiers.length === 0 && <NoData />}
            </TableContainer>

            <TablePagination
              component="div"
              labelRowsPerPage={generalT("rowsPerPage")}
              count={modifiersPagination?.total ?? modifiers.length}
              page={
                Math.max(
                  (modifiersPagination?.current ?? modifiersPage + 1) - 1,
                  0
                )
              }
              onPageChange={(_, newPage) => setModifiersPage(newPage)}
              rowsPerPage={modifiersPagination?.per ?? modifiersPerPage}
              onRowsPerPageChange={(event) => {
                setModifiersPerPage(parseInt(event.target.value, 10));
                setModifiersPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Box>

          {/* Footer */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            pt={2}
            borderTop="1px solid #EAECF0"
          >
            <Typography fontSize={14} color="#667085">
              {t("foodicsWorkspace.editImport.selectedOf", {
                selected: selectedModifierIds.length,
                total: seenModifierIdsRef.current.size,
              })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="tertiary"
                onClick={() => setActiveStep(2)}
                disabled={importStatus === "pending"}
              >
                {generalT("back")}
              </Button>
              <Button
                variant="contained"
                disabled={
                  modifiersRecipeMode === null || importStatus === "pending"
                }
                startIcon={
                  importStatus === "pending" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )
                }
                endIcon={forwardArrow}
                onClick={handleModifiersImport}
              >
                {t("foodicsWorkspace.editImport.importAndFinish")}
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default FoodicsMenuImportEdit;
